import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import { FormattedMessage } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { format, parseISO } from "date-fns";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import DataLoadError from "../../components/DataLoadError/DataLoadError";
import TooltipHelp from "../../components/Help/ToolTipHelp";
import { GetTeacherInformation } from "../../actions/teacherQueries";
import { getInitials } from "../../Utilities/nameHelper";
import { compareByName } from "../../Utilities/sortHelper";
import useTeacherNavStore from "../../stores/useTeacherNavStore";

const PermissionRow = ({ titleId, tooltipId, granted }) => (
  <div className="d-flex align-items-center py-2">
    <FontAwesomeIcon
      icon={granted ? "circle-check" : "circle-xmark"}
      className={granted ? "text-success me-2" : "text-muted me-2"}
    />
    <FormattedMessage id={titleId} />
    <TooltipHelp
      id={tooltipId}
      toolTipText={<FormattedMessage id={tooltipId} />}
      placement="right"
    />
    <span className="ms-2">
      (<FormattedMessage id={granted ? "Yes" : "No"} />)
    </span>
  </div>
);

export default function TeacherMyAccount() {
  const teacherInfo = GetTeacherInformation();
  const setTab = useTeacherNavStore((state) => state.setTab);

  if (teacherInfo.isPending) {
    return <Pacmanloader />;
  }

  if (teacherInfo.isError || !teacherInfo.data) {
    return <DataLoadError />;
  }

  const teacher = teacherInfo.data;

  const programmes = [...teacher.programmes]
    .sort(compareByName)
    .map((programme) => ({
      ...programme,
      classes: programme.classes
        .filter((schoolClass) =>
          schoolClass.teachers?.some(
            (classTeacher) => classTeacher.id === teacher.id,
          ),
        )
        .sort(compareByName),
    }))
    .filter((programme) => programme.classes.length > 0);
  const classCount = programmes.reduce(
    (sum, programme) => sum + programme.classes.length,
    0,
  );

  return (
    <div className="animated fadeIn">
      <Card>
        <CardBody className="px-4 px-md-5">
          <div className="d-flex align-items-center">
            <div className="account-avatar account-avatar--large me-3">
              {getInitials(teacher.name)}
            </div>
            <div>
              <h3 className="mb-1">{teacher.name}</h3>
              <div className="d-flex flex-wrap column-gap-4 row-gap-1 text-muted">
                <span>
                  <FontAwesomeIcon icon="envelope" className="me-1" />
                  {teacher.email}
                </span>
                {teacher.customer ? (
                  <span>
                    <FontAwesomeIcon icon="school" className="me-1" />
                    {teacher.customer.name}
                  </span>
                ) : (
                  ""
                )}
                {teacher.created ? (
                  <span>
                    <FontAwesomeIcon icon="calendar" className="me-1" />
                    <FormattedMessage id="AccountCreated" />{" "}
                    {format(parseISO(teacher.created), "yyyy-MM-dd")}
                  </span>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>

          <hr />

          <div className="my-account__section-label text-muted">
            <FormattedMessage id="Permissions" />
          </div>
          <PermissionRow
            titleId="FullTeacherAccess"
            tooltipId="ToolTipFullAccessTeacher"
            granted={teacher.fullAccess}
          />
          <PermissionRow
            titleId="Purchaser"
            tooltipId="ToolTipPurchaser"
            granted={teacher.purchaser}
          />

          <hr />

          <div className="my-account__section-label text-muted">
            <FormattedMessage id="MyAccountProgrammesAndClasses" />
            {programmes.length > 0 ? (
              <span className="my-account__count">
                (
                <FormattedMessage
                  id="ProgrammeCount"
                  values={{ count: programmes.length }}
                />
                {" · "}
                <FormattedMessage
                  id="ClassCount"
                  values={{ count: classCount }}
                />
                )
              </span>
            ) : (
              ""
            )}
          </div>
          {programmes.length > 0 ? (
            programmes.map((programme) => (
              <div key={programme.id} className="py-2">
                <div className="my-account__programme-name">
                  {programme.name}
                  <span className="my-account__count text-muted">
                    (
                    <FormattedMessage
                      id="ClassCount"
                      values={{ count: programme.classes.length }}
                    />
                    )
                  </span>
                </div>
                <div className="my-account__class-list">
                  {programme.classes.map((schoolClass) => (
                    <Link
                      key={schoolClass.id}
                      to={`/teacher/programmeview/${programme.id}/${encodeURIComponent(programme.name)}/classview/${schoolClass.id}/${encodeURIComponent(schoolClass.name)}`}
                      className="my-account__class-chip"
                      onClick={() => setTab(1)}
                    >
                      {schoolClass.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="small text-muted">
              <FormattedMessage id="NotConnectedToAnyClass" />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
